import axios from 'axios';
import store from '../store/index.js';
import router from '../router.js';

const textingSetupPath = '/texting';

export default class ApiService {
  constructor () {
    this.http = axios.create();
    this.http.defaults.headers.post['Content-Type'] = 'application/json; charset=utf-8';
  }

  execute (body) {
    return this.http.post('/request', body, { withCredentials: true })
      .then(function (response) {
        if (isErrorResponse(response.data)) {
          store.dispatch('setError', { errorMessage: 'Error making server request. Please contact customer support.' });
        }
        return response.data;
      })
      .catch(function (error) {
        // unauthorized, redirect
        if (error.response && error.response.status === 401) {
          if (store.state.auth.isAuthenticated) {
            router.push({ path: '/unauthorized' });
          } else {
            router.push({ path: '/signin' });
          }

          return { isOK: false };
        }
        if (error.response && error.response.data) {
          store.dispatch('setError', error.response.data);
        }
        return { isOK: false, status: error.response.status };
      });
  }

  // the idea here is to be able to support GET & POST requests in the UIController. Type = GET/POST. Payload is optional
  buildRequestBody (queryString, type, api, payload) {
    return {
      QueryString: queryString,
      Type: type,
      payload: payload,
      api: api
    };
  }

  getProducts () {
    return this.execute(this.buildRequestBody('marketplace/products', 'GET', 'Licensing'));
  }

  getAgencyUser () {
    return this.execute(this.buildRequestBody('agency/user', 'GET', 'Licensing'));
  }

  getAgencyUsers () {
    return this.execute(this.buildRequestBody('agency/users', 'GET', 'Licensing'));
  }

  getInsureSignUsers () {
    return this.http.get('/InsureSign/users', { withCredentials: true })
      .then(function (response) {
        if (isErrorResponse(response.data)) {
          store.dispatch('setError', { errorMessage: 'Error making server request. Please contact customer support.' });
        }
        return response.data;
      })
      .catch(function (error) {
        // unauthorized, redirect
        if (error.response && error.response.status === 401) {
          router.push({ path: '/unauthorized' });
          return { isOK: false };
        }
        if (error.response && error.response.data) {
          store.dispatch('setError', error.response.data);
        }
        return { isOK: false, status: error.response.status };
      });
  }

  acceptDataShareTermsAndConditions (productId) {
    return this.execute(this.buildRequestBody(`marketplace/product/acceptTerms`, 'POST', 'Licensing', productId));
  }

  sentInterestNotifications (productId) {
    return this.execute(this.buildRequestBody(`marketplace/product/${productId}/interestNotification`, 'POST', 'Licensing'));
  }

  setSubscriptionDetailed (productId, subscribing, feedback = null, sendEmail = false) {
    const payload = {
      ProductId: productId,
      Event: subscribing ? 'Subscribe' : 'Unsubscribe',
      SendEmail: sendEmail
    };
    if (feedback) {
      payload.Feedback = feedback;
    }
    return this.execute(this.buildRequestBody(`marketplace/product/subscriptionEvent`, 'POST', 'Licensing', payload));
  }

  getAgency () {
    return this.execute(this.buildRequestBody('agency', 'GET', 'Licensing'));
  }

  enableHawkSoftCloud () {
    return this.execute(this.buildRequestBody('marketplace/cloud?enable=true', 'POST', 'Licensing'));
  }

  resendLink (emailAddress) {
    var encoded = encodeURIComponent(emailAddress);
    return this.http.post(`/InsureSign/activate/resend?email=${encoded}`, null, { withCredentials: true })
      .then(function (response) {
        if (isErrorResponse(response.data)) {
          store.dispatch('setError', { errorMessage: 'Error making server request. Please contact customer support.' });
        }
        return response.data;
      })
      .catch(function (error) {
        // unauthorized, redirect
        if (error.response && error.response.status === 401) {
          router.push({ path: '/unauthorized' });
          return { isOK: false };
        }
        if (error.response && error.response.data) {
          store.dispatch('setError', error.response.data);
        }
        return { isOK: false, status: error.response.status };
      });
  }

  saveInsureSignUsers (users) {
    users.forEach(x => { x.emailAddress = x.email; });

    return this.http.post('/InsureSign/group', users, { withCredentials: true })
      .then(function (response) {
        if (isErrorResponse(response.data)) {
          store.dispatch('setError', { errorMessage: 'Error making server request. Please contact customer support.' });
        }
        return response.data;
      })
      .catch(function (error) {
        // unauthorized, redirect
        if (error.response && error.response.status === 401) {
          router.push({ path: '/unauthorized' });
          return { isOK: false };
        }
        if (error.response && error.response.data) {
          store.dispatch('setError', error.response.data);
        }
        return { isOK: false, status: error.response.status };
      });
  }

  sendAgencyPurchasedEmail () {
    return this.http.post('/InsureSign/purchased', null, { withCredentials: true })
      .then(function (response) {
        if (isErrorResponse(response.data)) {
          store.dispatch('setError', { errorMessage: 'Error making server request. Please contact customer support.' });
        }
        return response.data;
      })
      .catch(function (error) {
        // unauthorized, redirect
        if (error.response && error.response.status === 401) {
          router.push({ path: '/unauthorized' });
          return { isOK: false };
        }
        if (error.response && error.response.data) {
          store.dispatch('setError', error.response.data);
        }
        return { isOK: false, status: error.response.status };
      });
  }

  createMarketplaceRegistration (email) {
    var formattedPayload = '"' + email + '"';
    return this.execute(this.buildRequestBody('registration', 'POST', 'Licensing', formattedPayload));
  }

  updateRegistrationEmail (email) {
    const formattedPayload = '"' + email + '"';
    return this.execute(this.buildRequestBody('registration/update', 'POST', 'Licensing', formattedPayload));
  }

  getMainContacts (agencyId) {
    return this.execute(this.buildRequestBody(`api/Contact/${agencyId}/Emails`, 'GET', 'CRM'));
  }

  getRegistrationStatus () {
    return this.execute(this.buildRequestBody('registration/status', 'GET', 'Licensing'));
  }

  confirmAuthCode (authCode) {
    var formattedPayload = '"' + authCode + '"';
    return this.execute(this.buildRequestBody('registration/confirm', 'POST', 'Licensing', formattedPayload));
  }

  resendAuthCode () {
    return this.execute(this.buildRequestBody('registration/resend-confirmation', 'GET', 'Licensing'));
  }

  getFeatureSwitches () {
    return this.http.get('/featureSwitches', { withCredentials: true });
  }

  getTextingSetupUrl () {
    return this.http.get('/textingSetupUrl', { withCredentials: true });
  }

  getAgencyVersion () {
    return this.http.get('/version', { withCredentials: true });
  }

  getAgencySeats (agencyId) {
    return this.execute(this.buildRequestBody(`api/DateCode/Client/Seats/${agencyId}`, 'GET', 'CRM'));
  }

  createAgencySeatRequest (payload) {
    return this.execute(this.buildRequestBody('agency/seatChange', 'POST', 'Licensing', payload));
  }

  getAgencySeatRequests (agencyId) {
    return this.execute(this.buildRequestBody(`agency/seatChange`, 'GET', 'Licensing'));
  }

  updateAgencySeatRequest (payload) {
    return this.execute(this.buildRequestBody('agency/seatChange', 'PUT', 'Licensing', payload));
  }

  deleteAgencySeatRequest (id) {
    return this.execute(this.buildRequestBody(`agency/seatChange/${id}`, 'DELETE', 'Licensing'));
  }

  /* Texting Setup Commands */

  getTextingProfile () {
    return this.http.post(
      textingSetupPath,
      {
        command: 'GetAgency'
      },
      {
        withCredentials: true
      }
    );
  }

  searchNumbers (searchBy, areaCode, zipCode) {
    return this.http.post(
      textingSetupPath,
      {
        command: 'SearchNumbers',
        searchNumbers: {
          searchBy: searchBy,
          areaCode: areaCode,
          zipCode: zipCode,
          qty: 10
        }
      },
      {
        withCredentials: true
      });
  }

  orderNumber (searchBy, type, areaCode, zipCode) {
    return this.http.post(
      textingSetupPath,
      {
        command: 'OrderBandwidthNumber',
        orderBandwidthNumber: {
          searchBy: searchBy,
          assignToAgency: true,
          type: type,
          areaCode: areaCode,
          zipCode: zipCode
        }
      },
      {
        withCredentials: true
      });
  }

  setOrderInfo (agencyName, optInMode, timeStamp, contactNum) {
    return this.http.post(
      textingSetupPath,
      {
        command: 'AgencyInfoSet',
        agencyInfoSet: {
          name: agencyName,
          optInMode: optInMode,
          optInTimestamp: timeStamp,
          agencyContactNum: contactNum
        }
      },
      {
        withCredentials: true
      });
  }

  getOrderInfo () {
    return this.http.post(
      textingSetupPath,
      {
        command: 'GetOrderInformation'
      },
      {
        withCredentials: true
      });
  }

  checkNumAvailable (searchBy, areaCode, zipCode) {
    return this.http.post(
      textingSetupPath,
      {
        command: 'CheckBandwidthNumbers',
        checkBandwidthNumbers: {
          searchBy: searchBy,
          areaCode: areaCode,
          zipCode: zipCode
        }
      },
      {
        withCredentials: true
      }
    );
  }

  /***********/
  /* Texting */
  /***********/

  // Creates Job to generate export files
  triggerTextingDataExport () {
    return this.http.post(
      '/export/create',
      {
        subsystem: 1 // Texting
      },
      {
        withCredentials: true
      });
  }

  // Gets the blob metadata if it exists. Hits Marketplace API
  getTextingExportStatus () {
    return this.http.get('/export/status?subsystem=1');
  }

  // Returns file contents
  getTextingExportFile (progressCb) {
    return this.http.get('/export/download?subsystem=1', {
      responseType: 'blob',
      onDownloadProgress: function (event) {
        progressCb(event.loaded, event.total);
      }
    });
  }

  // Downloads the blob file to the browser
  async downloadTextingExportFile (progressCb) {
    try {
      const response = await this.getTextingExportFile(progressCb);
      if (response.status === 200) {
        createFilenameLink(response);
      }
      return true;
    } catch (err) {
      return err.statusText;
    }
  }
}

function createFilenameLink (response) {
  var headers = response.headers;
  var contentDisposition = headers['content-disposition'] || '';
  var filename = contentDisposition.split('filename=')[1].split(';')[0];
  filename = filename.replace(/"/g, '');

  var link = document.createElement('a');
  var fileURL = window.URL.createObjectURL(new Blob([response.data]));

  link.href = fileURL;
  link.setAttribute('download', filename);
  document.body.appendChild(link);

  link.click();
}

function isErrorResponse (responseModel) {
  // Is not OK or NoData
  return responseModel.code !== 0 && responseModel.code !== 2;
}
